// src/utils/simplifiedActivityLogger.js

import { collection, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Simplified Activity Logger
 * Tracks only the key user activities as requested
 */

// Simplified Activity Types
export const SIMPLIFIED_ACTIVITY_TYPES = {
  // Orders
  ORDER_CREATED: 'siparis_olusturuldu',
  ORDER_UPDATED: 'siparis_duzenlendi',
  ORDER_DELETED: 'siparis_silindi',
  
  // Customers
  CUSTOMER_CREATED: 'musteri_olusturuldu',
  CUSTOMER_UPDATED: 'musteri_duzenlendi',
  CUSTOMER_DELETED: 'musteri_silindi',
  
  // Payments
  PAYMENT_PROCESSED: 'odeme_islendi',
  
  // Stock
  STOCK_ADJUSTED: 'stok_ekleme_duzeltme',
  
  // Production
  PRODUCTION_ACTIVITY: 'uretim_aktivitesi'
};

// Simplified Activity Messages in Turkish
export const SIMPLIFIED_ACTIVITY_MESSAGES = {
  [SIMPLIFIED_ACTIVITY_TYPES.ORDER_CREATED]: 'Yeni sipariş oluşturdu',
  [SIMPLIFIED_ACTIVITY_TYPES.ORDER_UPDATED]: 'Siparişi düzenledi',
  [SIMPLIFIED_ACTIVITY_TYPES.ORDER_DELETED]: 'Siparişi sildi',
  
  [SIMPLIFIED_ACTIVITY_TYPES.CUSTOMER_CREATED]: 'Yeni müşteri oluşturdu',
  [SIMPLIFIED_ACTIVITY_TYPES.CUSTOMER_UPDATED]: 'Müşteri bilgilerini düzenledi',
  [SIMPLIFIED_ACTIVITY_TYPES.CUSTOMER_DELETED]: 'Müşteri sildi',
  
  [SIMPLIFIED_ACTIVITY_TYPES.PAYMENT_PROCESSED]: 'Ödeme işledi',
  
  [SIMPLIFIED_ACTIVITY_TYPES.STOCK_ADJUSTED]: 'Stok ekleme/düzeltme yaptı',
  
  [SIMPLIFIED_ACTIVITY_TYPES.PRODUCTION_ACTIVITY]: 'Üretim aktivitesi gerçekleştirdi'
};

/**
 * Log simplified activity to Firestore
 * @param {object} params - Activity parameters
 * @param {string} params.companyId - Company ID
 * @param {object} params.user - Current user object
 * @param {string} params.activityType - Type of activity (from SIMPLIFIED_ACTIVITY_TYPES)
 * @param {object} params.details - Additional details about the activity
 * @param {object} params.batch - Optional Firestore batch to use
 * @returns {Promise} - Promise that resolves when activity is logged
 */
export const logSimplifiedActivity = async ({
  companyId,
  user,
  activityType,
  details = {},
  batch = null
}) => {
  if (!companyId || !user || !activityType) {
    console.error('Missing required parameters for simplified activity logging');
    return;
  }

  try {
    const shouldCommitBatch = !batch;
    const activityBatch = batch || writeBatch(db);

    // Create activity log entry
    const activityRef = doc(collection(db, 'companies', companyId, 'simplifiedActivities'));
    
    const activityData = {
      // User Information
      userId: user.uid,
      userEmail: user.email,
      userName: user.email?.split('@')[0] || 'Bilinmeyen Kullanıcı',
      userRole: details.userRole || 'kullanıcı',
      
      // Activity Information
      activityType,
      activityMessage: SIMPLIFIED_ACTIVITY_MESSAGES[activityType] || 'Bilinmeyen işlem',
      
      // Timestamp
      timestamp: serverTimestamp(),
      formattedDate: new Date().toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      
      // Activity Details
      details: {
        ...details
      },
      
      // Additional Metadata
      actionSource: 'web_app',
      version: '1.0',
      companyId
    };

    activityBatch.set(activityRef, activityData);

    if (shouldCommitBatch) {
      await activityBatch.commit();
    }

    console.log(`Simplified activity logged: ${activityType} by ${user.email}`);
  } catch (error) {
    console.error('Error logging simplified activity:', error);
  }
};

/**
 * Helper functions for specific activities
 */

// Order activities
export const logOrderCreated = async (companyId, user, orderData) => {
  await logSimplifiedActivity({
    companyId,
    user,
    activityType: SIMPLIFIED_ACTIVITY_TYPES.ORDER_CREATED,
    details: {
      customerName: orderData.customerName,
      totalAmount: orderData.total,
      currency: orderData.currency,
      itemCount: orderData.lineItems?.length || 0,
      userRole: orderData.userRole || 'kullanıcı'
    }
  });
};

export const logOrderUpdated = async (companyId, user, orderData) => {
  await logSimplifiedActivity({
    companyId,
    user,
    activityType: SIMPLIFIED_ACTIVITY_TYPES.ORDER_UPDATED,
    details: {
      orderId: orderData.orderId,
      customerName: orderData.customerName,
      userRole: orderData.userRole || 'kullanıcı'
    }
  });
};

export const logOrderDeleted = async (companyId, user, orderData) => {
  await logSimplifiedActivity({
    companyId,
    user,
    activityType: SIMPLIFIED_ACTIVITY_TYPES.ORDER_DELETED,
    details: {
      orderId: orderData.orderId,
      customerName: orderData.customerName,
      userRole: orderData.userRole || 'kullanıcı'
    }
  });
};

// Customer activities
export const logCustomerCreated = async (companyId, user, customerData) => {
  await logSimplifiedActivity({
    companyId,
    user,
    activityType: SIMPLIFIED_ACTIVITY_TYPES.CUSTOMER_CREATED,
    details: {
      customerName: customerData.name,
      customerType: customerData.type,
      userRole: customerData.userRole || 'kullanıcı'
    }
  });
};

export const logCustomerUpdated = async (companyId, user, customerData) => {
  await logSimplifiedActivity({
    companyId,
    user,
    activityType: SIMPLIFIED_ACTIVITY_TYPES.CUSTOMER_UPDATED,
    details: {
      customerId: customerData.customerId,
      customerName: customerData.name,
      userRole: customerData.userRole || 'kullanıcı'
    }
  });
};

export const logCustomerDeleted = async (companyId, user, customerData) => {
  await logSimplifiedActivity({
    companyId,
    user,
    activityType: SIMPLIFIED_ACTIVITY_TYPES.CUSTOMER_DELETED,
    details: {
      customerId: customerData.customerId,
      customerName: customerData.name,
      userRole: customerData.userRole || 'kullanıcı'
    }
  });
};

// Payment activities
export const logPaymentProcessed = async (companyId, user, paymentData) => {
  await logSimplifiedActivity({
    companyId,
    user,
    activityType: SIMPLIFIED_ACTIVITY_TYPES.PAYMENT_PROCESSED,
    details: {
      orderId: paymentData.orderId,
      paymentAmount: paymentData.amount,
      currency: paymentData.currency,
      customerName: paymentData.customerName,
      userRole: paymentData.userRole || 'kullanıcı'
    }
  });
};

// Stock activities
export const logStockAdjusted = async (companyId, user, stockData) => {
  await logSimplifiedActivity({
    companyId,
    user,
    activityType: SIMPLIFIED_ACTIVITY_TYPES.STOCK_ADJUSTED,
    details: {
      productName: stockData.productName,
      quantity: stockData.quantity,
      unit: stockData.unit,
      adjustmentType: stockData.adjustmentType, // 'entry' or 'exit'
      userRole: stockData.userRole || 'kullanıcı'
    }
  });
};

// Production activities
export const logProductionActivity = async (companyId, user, productionData) => {
  await logSimplifiedActivity({
    companyId,
    user,
    activityType: SIMPLIFIED_ACTIVITY_TYPES.PRODUCTION_ACTIVITY,
    details: {
      productName: productionData.productName,
      quantity: productionData.quantity,
      activityType: productionData.activityType, // specific production activity
      userRole: productionData.userRole || 'kullanıcı'
    }
  });
};

const simplifiedActivityLogger = {
  logSimplifiedActivity,
  logOrderCreated,
  logOrderUpdated,
  logOrderDeleted,
  logCustomerCreated,
  logCustomerUpdated,
  logCustomerDeleted,
  logPaymentProcessed,
  logStockAdjusted,
  logProductionActivity,
  SIMPLIFIED_ACTIVITY_TYPES,
  SIMPLIFIED_ACTIVITY_MESSAGES
};

export default simplifiedActivityLogger;